import React, { useState, useEffect } from 'react';
import type { DiaryItem, Activity, SubType, Supplement } from '../models/diary';
import { diaryService, ExistingActivity, ExistingSupplement } from '../services/diaryService';

interface DiaryItemCreateModalProps {
  open: boolean;
  onSave: (newItem: Partial<DiaryItem>) => void;
  onCancel: () => void;
  defaultDayOfWeek?: string;
}

const DiaryItemCreateModal: React.FC<DiaryItemCreateModalProps> = ({ 
  open, 
  onSave, 
  onCancel, 
  defaultDayOfWeek = 'Monday' 
}) => {
  const [itemType, setItemType] = useState<'MealItem' | 'FitnessItem' | 'SupplementItem'>('FitnessItem');
  const [formData, setFormData] = useState({
    name: '',
    selectedDays: [defaultDayOfWeek] as string[],
    timeOfDay: 'Morning',
    subtype: 'gym' as SubType,
    order: 1,
    hidden: false,
    activities: [] as Activity[],
    supplements: [] as Supplement[]
  });

  // State for existing items from database
  const [existingActivities, setExistingActivities] = useState<ExistingActivity[]>([]);
  const [existingSupplements, setExistingSupplements] = useState<ExistingSupplement[]>([]);
  const [loadingExistingItems, setLoadingExistingItems] = useState(false);

  // State for activity selection mode
  const [activitySelectionMode, setActivitySelectionMode] = useState<'existing' | 'new'>('existing');
  const [selectedExistingActivityIds, setSelectedExistingActivityIds] = useState<number[]>([]);

  // State for supplement selection mode  
  const [supplementSelectionMode, setSupplementSelectionMode] = useState<'existing' | 'new'>('existing');
  const [selectedExistingSupplementIds, setSelectedExistingSupplementIds] = useState<number[]>([]);

  // Fetch existing items when modal opens
  useEffect(() => {
    if (open) {
      const fetchExistingItems = async () => {
        setLoadingExistingItems(true);
        try {
          const [activities, supplements] = await Promise.all([
            diaryService.getExistingActivities(),
            diaryService.getExistingSupplements()
          ]);
          setExistingActivities(activities);
          setExistingSupplements(supplements);
        } catch (error) {
          console.error('Error fetching existing items:', error);
        } finally {
          setLoadingExistingItems(false);
        }
      };

      fetchExistingItems();
    }
  }, [open]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setFormData({
        name: '',
        selectedDays: [defaultDayOfWeek],
        timeOfDay: 'Morning',
        subtype: (itemType === 'FitnessItem' ? 'gym' : itemType === 'MealItem' ? 'Breakfast' : 'supplementation') as SubType,
        order: 1,
        hidden: false,
        activities: [],
        supplements: []
      });
      // Reset selection states
      setActivitySelectionMode('existing');
      setSupplementSelectionMode('existing');
      setSelectedExistingActivityIds([]);
      setSelectedExistingSupplementIds([]);
    }
  }, [open, defaultDayOfWeek, itemType]);

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle day selection (multiple days)
  const handleDayToggle = (day: string) => {
    setFormData(prev => {
      const selectedDays = prev.selectedDays.includes(day)
        ? prev.selectedDays.filter(d => d !== day)
        : [...prev.selectedDays, day];
      return { ...prev, selectedDays };
    });
  };

  // Activity management functions
  const handleActivityChange = (activityIndex: number, field: keyof Activity, value: string) => {
    const updatedActivities = [...formData.activities];
    updatedActivities[activityIndex] = {
      ...updatedActivities[activityIndex],
      [field]: value
    };
    setFormData(prev => ({ ...prev, activities: updatedActivities }));
  };

  const addActivity = () => {
    const newActivity: Activity = {
      name: '',
      type: '',
      details: '',
      activity_level: ''
    };
    setFormData(prev => ({ 
      ...prev, 
      activities: [...prev.activities, newActivity] 
    }));
  };

  const removeActivity = (activityIndex: number) => {
    const updatedActivities = [...formData.activities];
    updatedActivities.splice(activityIndex, 1);
    setFormData(prev => ({ ...prev, activities: updatedActivities }));
  };

  // Supplement management functions
  const handleSupplementChange = (supplementIndex: number, field: keyof Supplement, value: string) => {
    const updatedSupplements = [...formData.supplements];
    updatedSupplements[supplementIndex] = {
      ...updatedSupplements[supplementIndex],
      [field]: value
    };
    setFormData(prev => ({ ...prev, supplements: updatedSupplements }));
  };

  const addSupplement = () => {
    const newSupplement: Supplement = {
      name: '',
      description: '',
      shopUrl: ''
    };
    setFormData(prev => ({ 
      ...prev, 
      supplements: [...prev.supplements, newSupplement] 
    }));
  };

  const removeSupplement = (supplementIndex: number) => {
    const updatedSupplements = [...formData.supplements];
    updatedSupplements.splice(supplementIndex, 1);
    setFormData(prev => ({ ...prev, supplements: updatedSupplements }));
  };

  // Helper functions for existing item selection
  const handleExistingActivitySelection = (activityId: number) => {
    const existingActivity = existingActivities.find(a => a.id === activityId);
    if (!existingActivity) return;

    // Convert existing activity to Activity format and add to selected IDs
    const activity: Activity = {
      id: existingActivity.id,
      name: existingActivity.name,
      type: '', // Not used for existing activities
      details: existingActivity.details,
      activity_level: existingActivity.activity_level
    };

    setFormData(prev => ({ 
      ...prev, 
      activities: [...prev.activities, activity] 
    }));
    setSelectedExistingActivityIds(prev => [...prev, activityId]);
  };

  const handleExistingSupplementSelection = (supplementId: number) => {
    const existingSupplement = existingSupplements.find(s => s.id === supplementId);
    if (!existingSupplement) return;

    // Convert existing supplement to Supplement format and add to selected IDs
    const supplement: Supplement = {
      id: existingSupplement.id,
      name: existingSupplement.name,
      description: existingSupplement.description,
      shopUrl: existingSupplement.shop_url || undefined
    };

    setFormData(prev => ({ 
      ...prev, 
      supplements: [...prev.supplements, supplement] 
    }));
    setSelectedExistingSupplementIds(prev => [...prev, supplementId]);
  };

  const removeExistingActivity = (activityId: number) => {
    setFormData(prev => ({ 
      ...prev, 
      activities: prev.activities.filter(a => a.id !== activityId) 
    }));
    setSelectedExistingActivityIds(prev => prev.filter(id => id !== activityId));
  };

  const removeExistingSupplement = (supplementId: number) => {
    setFormData(prev => ({ 
      ...prev, 
      supplements: prev.supplements.filter(s => s.id !== supplementId) 
    }));
    setSelectedExistingSupplementIds(prev => prev.filter(id => id !== supplementId));
  };

  const handleSave = () => {
    // Validate that at least one day is selected
    if (formData.selectedDays.length === 0) {
      alert('Por favor selecciona al menos un día de la semana');
      return;
    }

    // Create one DiaryItem for each selected day
    formData.selectedDays.forEach(day => {
      const newItem: Partial<DiaryItem> = {
        ...formData,
        dayOfWeek: day,
        type: itemType
      };
      // Remove selectedDays from the item since it's not part of DiaryItem interface
      const { selectedDays, ...itemWithoutSelectedDays } = newItem as any;
      onSave(itemWithoutSelectedDays);
    });
  };

  if (!open) return null;

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100vw', 
      height: '100vh', 
      background: 'rgba(0,0,0,0.18)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000 
    }}>
      <div style={{ 
        background: '#fff', 
        borderRadius: 12, 
        padding: 32, 
        minWidth: 400, 
        maxWidth: 500, 
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 32px rgba(0,0,0,0.13)' 
      }}>
        <h2 style={{ marginTop: 0, marginBottom: 18 }}>Crear Elemento del Diario</h2>
        
        {/* Item Type Selection */}
        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>Tipo de Elemento:</span>
          <select 
            value={itemType} 
            onChange={e => setItemType(e.target.value as any)}
            style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
          >
            <option value="FitnessItem">Ejercicio</option>
            <option value="MealItem">Comida</option>
            <option value="SupplementItem">Suplemento</option>
          </select>
        </label>

        {/* Basic Fields */}
        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>Nombre:</span>
          <input 
            value={formData.name} 
            onChange={e => handleFieldChange('name', e.target.value)} 
            style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4 }} 
            placeholder="Nombre del Item"
          />
        </label>

        <div style={{ marginBottom: 10 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Días de la semana:</span>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: 8, 
            border: '1px solid #ccc', 
            borderRadius: 4, 
            padding: 12 
          }}>
            {[
              { value: 'Monday', label: 'Lunes' },
              { value: 'Tuesday', label: 'Martes' },
              { value: 'Wednesday', label: 'Miércoles' },
              { value: 'Thursday', label: 'Jueves' },
              { value: 'Friday', label: 'Viernes' },
              { value: 'Saturday', label: 'Sábado' },
              { value: 'Sunday', label: 'Domingo' }
            ].map(day => (
              <label key={day.value} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input 
                  type="checkbox"
                  checked={formData.selectedDays.includes(day.value)}
                  onChange={() => handleDayToggle(day.value)}
                  style={{ marginRight: 6 }}
                />
                <span style={{ fontSize: 14 }}>{day.label}</span>
              </label>
            ))}
          </div>
          {formData.selectedDays.length === 0 && (
            <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
              Selecciona al menos un día
            </div>
          )}
        </div>

        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>Momento del día:</span>
          <select 
            value={formData.timeOfDay} 
            onChange={e => handleFieldChange('timeOfDay', e.target.value)}
            style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
          >
            <option value="Morning">Mañana</option>
            <option value="Afternoon">Tarde</option>
            <option value="Evening">Noche</option>
            <option value="Other">Otro</option>
          </select>
        </label>

        {/* Subtype based on item type */}
        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
            {itemType === 'FitnessItem' ? 'Tipo de ejercicio:' : 
             itemType === 'MealItem' ? 'Tipo de comida:' : 'Tipo de suplemento:'}
          </span>
          <select 
            value={formData.subtype} 
            onChange={e => handleFieldChange('subtype', e.target.value)}
            style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
          >
            {itemType === 'FitnessItem' && (
              <>
                <option value="cardio">Cardio</option>
                <option value="gym">Gym</option>
                <option value="sport">Sport</option>
                <option value="swimming">Swimming</option>
                <option value="yoga">Yoga</option>
                <option value="pilates">Pilates</option>
                <option value="walking">Walking</option>
              </>
            )}
            {itemType === 'MealItem' && (
              <>
                <option value="Breakfast">Desayuno</option>
                <option value="Lunch">Almuerzo</option>
                <option value="Dinner">Cena</option>
                <option value="Snack">Snack</option>
                <option value="Other">Otro</option>
              </>
            )}
            {itemType === 'SupplementItem' && (
              <option value="supplementation">Suplementación</option>
            )}
          </select>
        </label>

        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>Orden:</span>
          <input 
            type="number" 
            value={formData.order} 
            onChange={e => handleFieldChange('order', parseInt(e.target.value))} 
            style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4 }} 
            min="1" 
          />
        </label>

        <label style={{ display: 'flex', alignItems: 'center', marginBottom: 15 }}>
          <input 
            type="checkbox" 
            checked={formData.hidden} 
            onChange={e => handleFieldChange('hidden', e.target.checked)} 
            style={{ marginRight: 8 }} 
          />
          <span style={{ fontWeight: 600 }}>Oculto</span>
        </label>

        {/* Activities Section (only for FitnessItem) */}
        {itemType === 'FitnessItem' && (
          <div style={{ marginBottom: 15 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600 }}>Actividades:</span>
            </div>

            {/* Activity Selection Mode Toggle */}
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                <input 
                  type="radio" 
                  name="activityMode" 
                  checked={activitySelectionMode === 'existing'} 
                  onChange={() => setActivitySelectionMode('existing')}
                  style={{ marginRight: 6 }}
                />
                <span style={{ fontSize: 14 }}>Seleccionar de existentes</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input 
                  type="radio" 
                  name="activityMode" 
                  checked={activitySelectionMode === 'new'} 
                  onChange={() => setActivitySelectionMode('new')}
                  style={{ marginRight: 6 }}
                />
                <span style={{ fontSize: 14 }}>Crear nueva actividad</span>
              </label>
            </div>

            {/* Existing Activities Selection */}
            {activitySelectionMode === 'existing' && (
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>Seleccionar actividad:</span>
                  {loadingExistingItems ? (
                    <div style={{ padding: 8, fontSize: 13, color: '#666' }}>Cargando actividades...</div>
                  ) : (
                    <select 
                      onChange={e => e.target.value && handleExistingActivitySelection(parseInt(e.target.value))}
                      style={{ width: '100%', marginTop: 2, padding: 4, border: '1px solid #ccc', borderRadius: 3, fontSize: 13 }}
                      value=""
                    >
                      <option value="">Seleccionar una actividad</option>
                      {existingActivities
                        .filter(activity => !selectedExistingActivityIds.includes(activity.id))
                        .map(activity => (
                          <option key={activity.id} value={activity.id}>
                            {activity.name} ({activity.activity_level})
                          </option>
                        ))
                      }
                    </select>
                  )}
                </label>
              </div>
            )}

            {/* New Activity Creation */}
            {activitySelectionMode === 'new' && (
              <div style={{ marginBottom: 10 }}>
                <button 
                  type="button" 
                  onClick={addActivity}
                  style={{ 
                    background: '#38b2ac', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: 4, 
                    padding: '4px 8px', 
                    fontSize: 12, 
                    cursor: 'pointer',
                    marginBottom: 8
                  }}
                >
                  + Agregar Nueva Actividad
                </button>
              </div>
            )}

            {/* Display Selected/Created Activities */}
            {formData.activities.map((activity, index) => {
              const isExisting = activity.id && selectedExistingActivityIds.includes(activity.id);
              return (
                <div key={activity.id || index} style={{ 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 6, 
                  padding: 8, 
                  marginBottom: 8, 
                  backgroundColor: isExisting ? '#f0f9ff' : '#f9f9f9' 
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>
                      {isExisting ? `Actividad Existente: ${activity.name}` : `Actividad ${index + 1}`}
                    </span>
                    <button 
                      type="button" 
                      onClick={() => isExisting ? removeExistingActivity(activity.id!) : removeActivity(index)}
                      style={{ 
                        background: '#ef4444', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 4, 
                        padding: '2px 6px', 
                        fontSize: 12, 
                        cursor: 'pointer' 
                      }}
                    >
                      Eliminar
                    </button>
                  </div>
                  
                  {isExisting ? (
                    // Read-only display for existing activities
                    <div>
                      <div style={{ fontSize: 13, marginBottom: 4 }}>
                        <strong>Nivel:</strong> {activity.activity_level}
                      </div>
                      <div style={{ fontSize: 13, marginBottom: 4 }}>
                        <strong>Detalles:</strong> {activity.details}
                      </div>
                    </div>
                  ) : (
                    // Editable fields for new activities
                    <>
                      <label style={{ display: 'block', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Nombre:</span>
                        <input 
                          value={activity.name} 
                          onChange={e => handleActivityChange(index, 'name', e.target.value)}
                          style={{ 
                            width: '100%', 
                            marginTop: 2, 
                            padding: 4, 
                            border: '1px solid #ccc', 
                            borderRadius: 3, 
                            fontSize: 13 
                          }} 
                        />
                      </label>
                      
                      <label style={{ display: 'block', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Nivel de actividad:</span>
                        <select 
                          value={activity.activity_level} 
                          onChange={e => handleActivityChange(index, 'activity_level', e.target.value)}
                          style={{ 
                            width: '100%', 
                            marginTop: 2, 
                            padding: 4, 
                            border: '1px solid #ccc', 
                            borderRadius: 3, 
                            fontSize: 13 
                          }}
                        >
                          <option value="">Seleccionar nivel</option>
                          <option value="low">Bajo</option>
                          <option value="medium">Medio</option>
                          <option value="high">Alto</option>
                        </select>
                      </label>
                      
                      <label style={{ display: 'block', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Detalles:</span>
                        <textarea 
                          value={activity.details} 
                          onChange={e => handleActivityChange(index, 'details', e.target.value)}
                          style={{ 
                            width: '100%', 
                            marginTop: 2, 
                            padding: 4, 
                            border: '1px solid #ccc', 
                            borderRadius: 3, 
                            fontSize: 13, 
                            minHeight: 40, 
                            resize: 'vertical' 
                          }} 
                        />
                      </label>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Supplements Section (only for SupplementItem) */}
        {itemType === 'SupplementItem' && (
          <div style={{ marginBottom: 15 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600 }}>Suplementos:</span>
            </div>

            {/* Supplement Selection Mode Toggle */}
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                <input 
                  type="radio" 
                  name="supplementMode" 
                  checked={supplementSelectionMode === 'existing'} 
                  onChange={() => setSupplementSelectionMode('existing')}
                  style={{ marginRight: 6 }}
                />
                <span style={{ fontSize: 14 }}>Seleccionar de existentes</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input 
                  type="radio" 
                  name="supplementMode" 
                  checked={supplementSelectionMode === 'new'} 
                  onChange={() => setSupplementSelectionMode('new')}
                  style={{ marginRight: 6 }}
                />
                <span style={{ fontSize: 14 }}>Crear nuevo suplemento</span>
              </label>
            </div>

            {/* Existing Supplements Selection */}
            {supplementSelectionMode === 'existing' && (
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>Seleccionar suplemento:</span>
                  {loadingExistingItems ? (
                    <div style={{ padding: 8, fontSize: 13, color: '#666' }}>Cargando suplementos...</div>
                  ) : (
                    <select 
                      onChange={e => e.target.value && handleExistingSupplementSelection(parseInt(e.target.value))}
                      style={{ width: '100%', marginTop: 2, padding: 4, border: '1px solid #ccc', borderRadius: 3, fontSize: 13 }}
                      value=""
                    >
                      <option value="">Seleccionar un suplemento</option>
                      {existingSupplements
                        .filter(supplement => !selectedExistingSupplementIds.includes(supplement.id))
                        .map(supplement => (
                          <option key={supplement.id} value={supplement.id}>
                            {supplement.name}
                          </option>
                        ))
                      }
                    </select>
                  )}
                </label>
              </div>
            )}

            {/* New Supplement Creation */}
            {supplementSelectionMode === 'new' && (
              <div style={{ marginBottom: 10 }}>
                <button 
                  type="button" 
                  onClick={addSupplement}
                  style={{ 
                    background: '#38b2ac', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: 4, 
                    padding: '4px 8px', 
                    fontSize: 12, 
                    cursor: 'pointer',
                    marginBottom: 8
                  }}
                >
                  + Agregar Nuevo Suplemento
                </button>
              </div>
            )}

            {/* Display Selected/Created Supplements */}
            {formData.supplements.map((supplement, index) => {
              const isExisting = supplement.id && selectedExistingSupplementIds.includes(supplement.id);
              return (
                <div key={supplement.id || index} style={{ 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 6, 
                  padding: 8, 
                  marginBottom: 8, 
                  backgroundColor: isExisting ? '#f0f9ff' : '#f9f9f9' 
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>
                      {isExisting ? `Suplemento Existente: ${supplement.name}` : `Suplemento ${index + 1}`}
                    </span>
                    <button 
                      type="button" 
                      onClick={() => isExisting ? removeExistingSupplement(supplement.id!) : removeSupplement(index)}
                      style={{ 
                        background: '#ef4444', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 4, 
                        padding: '2px 6px', 
                        fontSize: 12, 
                        cursor: 'pointer' 
                      }}
                    >
                      Eliminar
                    </button>
                  </div>
                  
                  {isExisting ? (
                    // Read-only display for existing supplements
                    <div>
                      <div style={{ fontSize: 13, marginBottom: 4 }}>
                        <strong>Descripción:</strong> {supplement.description}
                      </div>
                      {supplement.shopUrl && (
                        <div style={{ fontSize: 13, marginBottom: 4 }}>
                          <strong>URL de compra:</strong> 
                          <a href={supplement.shopUrl} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 4, color: '#38b2ac' }}>
                            {supplement.shopUrl}
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Editable fields for new supplements
                    <>
                      <label style={{ display: 'block', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Nombre:</span>
                        <input 
                          value={supplement.name} 
                          onChange={e => handleSupplementChange(index, 'name', e.target.value)}
                          style={{ 
                            width: '100%', 
                            marginTop: 2, 
                            padding: 4, 
                            border: '1px solid #ccc', 
                            borderRadius: 3, 
                            fontSize: 13 
                          }} 
                        />
                      </label>
                      
                      <label style={{ display: 'block', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Descripción:</span>
                        <textarea 
                          value={supplement.description} 
                          onChange={e => handleSupplementChange(index, 'description', e.target.value)}
                          style={{ 
                            width: '100%', 
                            marginTop: 2, 
                            padding: 4, 
                            border: '1px solid #ccc', 
                            borderRadius: 3, 
                            fontSize: 13, 
                            minHeight: 40, 
                            resize: 'vertical' 
                          }} 
                        />
                      </label>
                      
                      <label style={{ display: 'block', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>URL de compra (opcional):</span>
                        <input 
                          value={supplement.shopUrl || ''} 
                          onChange={e => handleSupplementChange(index, 'shopUrl', e.target.value)}
                          style={{ 
                            width: '100%', 
                            marginTop: 2, 
                            padding: 4, 
                            border: '1px solid #ccc', 
                            borderRadius: 3, 
                            fontSize: 13 
                          }} 
                          placeholder="https://..."
                        />
                      </label>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
          <button 
            onClick={onCancel} 
            style={{ 
              background: '#eee', 
              border: 'none', 
              borderRadius: 6, 
              padding: '8px 18px', 
              fontWeight: 600, 
              cursor: 'pointer' 
            }}
          >
            Cancelar
          </button>
          <button 
            onClick={handleSave} 
            disabled={formData.selectedDays.length === 0}
            style={{ 
              background: formData.selectedDays.length === 0 ? '#ccc' : '#38b2ac', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 6, 
              padding: '8px 18px', 
              fontWeight: 600, 
              cursor: formData.selectedDays.length === 0 ? 'not-allowed' : 'pointer',
              opacity: formData.selectedDays.length === 0 ? 0.6 : 1
            }}
          >
            {formData.selectedDays.length === 0 
              ? 'Selecciona días' 
              : formData.selectedDays.length === 1 
                ? 'Crear Elemento' 
                : `Crear ${formData.selectedDays.length} Elementos`
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiaryItemCreateModal;
